import { useEffect, useRef } from "react";
import { DialerApiError, type DialerApi } from "../api/types";

type Opts = {
  intervalMs?: number;
  pauseCapMs?: number;
  /** First retry delay after a transient failure; doubles up to 4× (1 s, 2 s, 4 s, 4 s…). */
  retryBaseMs?: number;
  /** Lease length, used to estimate locked_until until the first renewal returns the real one. */
  leaseMs?: number;
  onLost?: () => void;
  onPauseReleased?: () => void;
};

/** Floor on the delay between ticks, so a bad option can never spin a hot loop. */
const MIN_DELAY_MS = 10;
const MAX_BACKOFF_STEPS = 3;

type CardState = {
  companyId: number | null;
  pausedMs: number;
  leaseUntil: number;
  ended: boolean;
};

/**
 * Renews the lease every 3 min while the card is open (spec 04 §2.4).
 *
 * - Paused: renewal continues until the TOTAL paused time on this card (across pause/resume
 *   toggles) reaches the cap (10 min), then the lease is released once and `onPauseReleased` fires.
 * - Transient errors (network, server) are retried with backoff (1 s, 2 s, 4 s…), each retry
 *   scheduled before the lease runs out. `onLost` fires once, only on `lease_lost` or when
 *   locked_until has passed without a successful renewal; renewal then stops.
 * - Ticks are chained (never overlapping) and stop on unmount.
 *
 * `api` must be stable (memoized, e.g. from `useDialerApi()`): it is an effect dependency, so a new
 * object on every render would restart the timer each render.
 */
/** Total pause on one card after which the lease is released (spec 04 §4.3 Paused). Also taught by /formation. */
export const PAUSE_CAP_MS = 600_000;

export function useLeaseRenewal(
  api: DialerApi,
  companyId: number | null,
  state: { paused: boolean; enabled: boolean },
  opts: Opts = {},
) {
  const {
    intervalMs = 180_000,
    pauseCapMs = PAUSE_CAP_MS,
    retryBaseMs = 1_000,
    leaseMs = 600_000,
  } = opts;
  const cbs = useRef(opts);
  useEffect(() => {
    cbs.current = opts;
  });
  // Per-card state that survives the effect re-running on each pause toggle.
  const card = useRef<CardState>({
    companyId: null,
    pausedMs: 0,
    leaseUntil: 0,
    ended: false,
  });

  useEffect(() => {
    if (!state.enabled || companyId === null) return;
    if (card.current.companyId !== companyId)
      card.current = {
        companyId,
        pausedMs: 0,
        leaseUntil: Date.now() + leaseMs,
        ended: false,
      };
    const c = card.current;
    if (c.ended) return;
    const pausedAt = state.paused ? Date.now() : null;
    const pausedTotal = () =>
      c.pausedMs + (pausedAt === null ? 0 : Date.now() - pausedAt);
    let stopped = false;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const end = () => {
      stopped = true;
      c.ended = true;
    };
    const schedule = (ms: number) => {
      if (!stopped)
        timer = setTimeout(() => void tick(), Math.max(ms, MIN_DELAY_MS));
    };
    const nextDelay = () =>
      pausedAt === null
        ? intervalMs
        : Math.min(intervalMs, pauseCapMs - pausedTotal());

    const tick = async () => {
      if (stopped) return;
      if (pausedAt !== null && pausedTotal() >= pauseCapMs) {
        end();
        try {
          await api.releaseLease(companyId);
        } catch {
          // the lease expires on its own; the card is told either way
        }
        cbs.current.onPauseReleased?.();
        return;
      }
      try {
        const until = Date.parse(await api.renewLease(companyId));
        if (stopped) return;
        c.leaseUntil = Number.isNaN(until) ? Date.now() + leaseMs : until;
        failures = 0;
        schedule(nextDelay());
      } catch (err) {
        if (stopped) return;
        const left = c.leaseUntil - Date.now();
        const lost = err instanceof DialerApiError && err.code === "lease_lost";
        if (lost || left <= 0) {
          end();
          cbs.current.onLost?.();
          return;
        }
        const backoff =
          retryBaseMs * 2 ** Math.min(failures, MAX_BACKOFF_STEPS - 1);
        failures += 1;
        schedule(Math.min(backoff, left - MIN_DELAY_MS));
      }
    };

    schedule(nextDelay());
    return () => {
      stopped = true;
      clearTimeout(timer);
      if (pausedAt !== null) c.pausedMs += Date.now() - pausedAt;
    };
  }, [
    api,
    companyId,
    state.enabled,
    state.paused,
    intervalMs,
    pauseCapMs,
    retryBaseMs,
    leaseMs,
  ]);
}
